/**
 * Stable fingerprints for comparing chapter manuscript bodies (ignores comment overlay marks).
 */

export function sortKeysDeep(x: unknown): unknown {
  if (x === null || typeof x !== 'object') return x;
  if (Array.isArray(x)) return x.map(sortKeysDeep);
  const o = x as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(o).sort()) {
    sorted[k] = sortKeysDeep(o[k]);
  }
  return sorted;
}

export function chapterBodyFingerprint(title: string, contentJson: string): string {
  try {
    const parsed = JSON.parse(contentJson) as unknown;
    return `${title}\n${JSON.stringify(sortKeysDeep(parsed))}`;
  } catch {
    return `${title}\n${contentJson}`;
  }
}

export type ChapterServerSyncAction = 'skip_unchanged' | 'skip_dirty' | 'apply';

/**
 * Decide whether a server chapter payload should replace the editor document.
 * Comment-only API touches bump `updatedAt` without changing body text — treat as unchanged.
 */
export function decideChapterServerSyncAction(input: {
  serverTitle: string;
  serverContentJson: string;
  baselineTitle: string;
  baselineContentJson: string;
  isDirty: boolean;
}): ChapterServerSyncAction {
  const serverFp = chapterBodyFingerprint(input.serverTitle, input.serverContentJson);
  const baselineFp = chapterBodyFingerprint(input.baselineTitle, input.baselineContentJson);
  if (serverFp === baselineFp) return 'skip_unchanged';
  if (input.isDirty) return 'skip_dirty';
  return 'apply';
}
